import { useState, useMemo } from "react";
import {
  Box, Text, HStack, VStack,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  IconButton, Icon, Button,
  NumberInput, NumberInputField,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight, FiLayers, FiPackage, FiEdit2, FiCheck, FiX, FiPlus, FiTrash2 } from "react-icons/fi";
import Select from "react-select";

const BRAND = "#C41E3A";

const rowKeyOf = (item, idx) =>
  `${item.runId ?? idx}-${item.product?.productId ?? idx}-${item.fileName ?? ""}`;

const addProductSelectStyles = {
  container: (base) => ({ ...base, minWidth: "260px" }),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? BRAND : "#E2E8F0",
    boxShadow: state.isFocused ? `0 0 0 1px ${BRAND}` : "none",
    minHeight: "32px",
  }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

export function ShelfSummaryTable({
  shelves, shelfIndex, onPrevShelf, onNextShelf, auditorName, auditedDate,
  onUpdateCount, savingRowKey,
  availabilityProducts, loadingProducts, onAddProduct, addingProductShelf,
  onDeleteProduct, deletingRowKey,
}) {
  const totalShelves = shelves?.length ?? 0;
  const shelf = totalShelves > 0 ? shelves[shelfIndex] : null;
  const rows = shelf?.productCounts ?? [];

  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState({ manualProductCount: "", aiProductCount: "" });

  const [showAddProduct, setShowAddProduct] = useState(false);
  const isAddingProduct = shelf != null && addingProductShelf === shelf.shelfNumber;

  const productOptions = useMemo(() => {
    const existingIds = new Set(
      (shelf?.productCounts ?? [])
        .map((p) => p.product?.productId)
        .filter((id) => id != null)
    );
    return (availabilityProducts ?? [])
      .filter((p) => !existingIds.has(p.productId))
      .map((p) => ({ value: p.productId, label: p.productName, product: p }));
  }, [availabilityProducts, shelf]);

  const handleSelectProduct = (option) => {
    if (!option || !shelf) return;
    setShowAddProduct(false);
    onAddProduct?.(shelf.shelfNumber, option.product);
  };

  const startEdit = (item, idx) => {
    setEditingKey(rowKeyOf(item, idx));
    setDraft({
      manualProductCount: item.manualProductCount ?? "",
      aiProductCount: item.aiProductCount ?? "",
    });
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft({ manualProductCount: "", aiProductCount: "" });
  };

  const saveEdit = async (item) => {
    const values = {
      manualProductCount: draft.manualProductCount === "" ? null : Number(draft.manualProductCount),
      aiProductCount: draft.aiProductCount === "" ? null : Number(draft.aiProductCount),
    };
    try {
      await onUpdateCount?.(shelf.shelfNumber, item, values);
      setEditingKey(null);
    } catch {
    }
  };

  return (
    <VStack spacing={3} align="stretch" flex="1">

      {/* Shelf navigation */}
      <HStack justify="space-between" wrap="wrap" rowGap={2}>
        <HStack spacing={2}>
          <Icon as={FiLayers} color={BRAND} />
          <Text fontWeight="700" fontSize="sm" color="gray.700">
            {shelf ? `Shelf ${shelf.shelfNumber}` : "No shelves"}
          </Text>
        </HStack>
        {(auditorName || auditedDate) && (
          <HStack spacing={6}>
            <Text fontSize="xs" color="gray.600">
              <Text as="span" fontWeight="800">AuditorName:</Text> {auditorName ?? "—"}
            </Text>
            <Text fontSize="xs" color="gray.600">
              <Text as="span" fontWeight="800">AuditedDate:</Text> {auditedDate ?? "—"}
            </Text>
          </HStack>
        )}
        {shelf && (
          showAddProduct ? (
            <HStack spacing={2}>
              <Select
                autoFocus
                openMenuOnFocus
                isLoading={loadingProducts}
                isDisabled={isAddingProduct}
                isClearable
                options={productOptions}
                placeholder="Search product..."
                onChange={handleSelectProduct}
                onBlur={() => setShowAddProduct(false)}
                noOptionsMessage={() =>
                  loadingProducts ? "Loading products..." : "No matching products"
                }
                styles={addProductSelectStyles}
              />
              <IconButton
                aria-label="Cancel add product" icon={<FiX />} size="sm"
                variant="ghost" onClick={() => setShowAddProduct(false)}
              />
            </HStack>
          ) : (
            <Button
              size="sm" leftIcon={<FiPlus />} colorScheme="red" variant="outline"
              isLoading={isAddingProduct} loadingText="Adding..."
              onClick={() => setShowAddProduct(true)}
            >
              Add Product
            </Button>
          )
        )}
      </HStack>

      {/* Table */}
      <Box
        flex="1" overflowY="auto" borderRadius="xl" boxShadow="md"
        border="1px solid" borderColor="gray.300"
        maxH={{ base: "360px", xl: "440px" }}
      >
        <TableContainer overflowX="auto">
          <Table size="sm" variant="unstyled" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <Thead position="sticky" top={0} zIndex={1}>
              <Tr>
                {[
                  { label: "Product Name", align: "left" },
                  { label: "Manual Count", align: "center" },
                  { label: "AI Count", align: "center" },
                  { label: "Actions", align: "center" },
                ].map(({ label, align }) => (
                  <Th
                    key={label}
                    bg="#C41E3A" color="white"
                    fontSize="11px" fontWeight="700"
                    textTransform="uppercase" letterSpacing="wider"
                    py={3} px={3} textAlign={align} borderBottom="none"
                  >
                    {label}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {rows.length === 0 ? (
                <Tr>
                  <Td colSpan={4} textAlign="center" py={12} px={3}>
                    <VStack spacing={2}>
                      <Icon as={FiPackage} boxSize={8} color="gray.300" />
                      <Text color="gray.400" fontSize="sm">No products on this shelf</Text>
                    </VStack>
                  </Td>
                </Tr>
              ) : (
                rows.map((item, idx) => {
                  const key = rowKeyOf(item, idx);
                  const isEditing = editingKey === key;
                  const isSaving = savingRowKey === key;
                  const isDeleting = deletingRowKey === key;

                  return (
                    <Tr
                      key={key}
                      bg={idx % 2 === 0 ? "white" : "gray.50"}
                      _hover={{ bg: "red.50" }}
                      transition="background 0.15s"
                    >
                      <Td py={3} px={3}>
                        <Text fontSize="13px" fontWeight="600" color="gray.800">
                          {item.product?.productName ?? "—"}
                        </Text>
                      </Td>

                      <Td py={3} px={3} textAlign="center">
                        {isEditing ? (
                          <NumberInput
                            size="sm" min={0} value={draft.manualProductCount}
                            onChange={(valStr) => setDraft((d) => ({ ...d, manualProductCount: valStr }))}
                            maxW="90px" mx="auto"
                          >
                            <NumberInputField textAlign="center" />
                          </NumberInput>
                        ) : item.manualProductCount == null ? (
                          <Text fontSize="13px" color="gray.400">—</Text>
                        ) : (
                          <Box
                            display="inline-block" px={3} py={1}
                            bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.100"
                          >
                            <Text fontSize="13px" fontWeight="700" color="blue.700">
                              {item.manualProductCount}
                            </Text>
                          </Box>
                        )}
                      </Td>

                      <Td py={3} px={3} textAlign="center">
                        {isEditing ? (
                          <NumberInput
                            size="sm" min={0} value={draft.aiProductCount}
                            onChange={(valStr) => setDraft((d) => ({ ...d, aiProductCount: valStr }))}
                            maxW="90px" mx="auto"
                          >
                            <NumberInputField textAlign="center" />
                          </NumberInput>
                        ) : item.aiProductCount == null ? (
                          <Text fontSize="13px" color="gray.400">—</Text>
                        ) : (
                          <Box
                            display="inline-block" px={3} py={1}
                            bg="purple.50" borderRadius="lg" border="1px solid" borderColor="purple.100"
                          >
                            <Text fontSize="13px" fontWeight="700" color="purple.700">
                              {item.aiProductCount}
                            </Text>
                          </Box>
                        )}
                      </Td>

                      <Td py={3} px={3} textAlign="center">
                        {isEditing ? (
                          <HStack spacing={1} justify="center">
                            <IconButton
                              aria-label="Save counts" icon={<FiCheck />} size="xs"
                              colorScheme="green" isLoading={isSaving}
                              onClick={() => saveEdit(item)}
                            />
                            <IconButton
                              aria-label="Cancel edit" icon={<FiX />} size="xs"
                              variant="ghost" isDisabled={isSaving}
                              onClick={cancelEdit}
                            />
                          </HStack>
                        ) : (
                          <HStack spacing={1} justify="center">
                            <IconButton
                              aria-label="Edit counts" icon={<FiEdit2 />} size="xs"
                              variant="ghost" colorScheme="gray"
                              isDisabled={isDeleting}
                              onClick={() => startEdit(item, idx)}
                            />
                            <IconButton
                              aria-label="Remove product" icon={<FiTrash2 />} size="xs"
                              variant="ghost" colorScheme="red"
                              isLoading={isDeleting}
                              onClick={() => onDeleteProduct?.(shelf.shelfNumber, item)}
                            />
                          </HStack>
                        )}
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
      <HStack justify="flex-end" spacing={2}>
          <IconButton
            aria-label="Previous shelf" icon={<FiChevronLeft />} size="xs"
            isDisabled={shelfIndex === 0}
            onClick={onPrevShelf}
          />
          <Text fontSize="sm" color="gray.600" fontWeight="600">
            Shelf {totalShelves ? shelfIndex + 1 : 0} of {totalShelves}
          </Text>
          <IconButton
            aria-label="Next shelf" icon={<FiChevronRight />} size="sm"
            isDisabled={shelfIndex >= totalShelves - 1}
            onClick={onNextShelf}
          />
        </HStack>
    </VStack>
  );
}